import { get, writable, type Writable } from 'svelte/store';

export const infinityListStore = writable<InfinityListStore>({});
export const infinityItemListCountStore = writable<InfinityListItemCountStore>({});
export const infinityListReloadStore = writable<InfinityListReloadStore>({});
export const infinityListItemStore = writable<InfinityListItemStore>({});

// region Types
type InfinityListStore = {
	[key: string]: InfinityList | undefined;
};

type InfinityListItemStore = {
	[key: string]: unknown[] | undefined;
};

type InfinityItemCount = {
	count: number;
	ended: boolean;
};

type InfinityListItemCountStore = {
	[key: string]: InfinityItemCount | undefined;
};

type InfinityListReloadStore = {
	[key: string]: boolean | undefined;
};

export type InfinityList = {
	sort: InfinityListSort | null;
	filters: Array<AppliedFilter>;
	search: string | null;
};

export type InfinityListSort = {
	field: string;
	direction: 'asc' | 'desc' | null;
};

export type InfinityListFilters = {
	[key: string]: InfinityListFilter;
};

export type InfinityListFilter = {
	default: string | null;
	multiValue: boolean;
	items: Array<{ value: string | null; label?: string }>;
};

// Applied filters are the filters that are currently applied to the list with their value
type AppliedFilter = {
	name: string;
	value: string | string[] | null;
};

export type ListStore<T> = {
	data: Array<T>;
	isFresh: boolean;
	apiSearchUrl: string;
};

// endregion

export function getInfinityList(listId: string, sort?: InfinityListSort | null): InfinityList;
export function getInfinityList(
	store: InfinityListStore,
	listId: string,
	sort?: InfinityListSort | null
): InfinityList;
export function getInfinityList(
	storeOrListId: InfinityListStore | string,
	listIdOrSort?: string | InfinityListSort | null,
	defaultSort: InfinityListSort | null = null
): InfinityList {
	if (typeof storeOrListId === 'string') {
		const listId = storeOrListId;
		const sort = (listIdOrSort as InfinityListSort | null) ?? null;
		const lists = get(infinityListStore);
		return (
			lists[listId] ?? {
				sort,
				filters: [],
				search: null
			}
		);
	}

	const store = storeOrListId;
	const listId = listIdOrSort as string;
	return (
		store[listId] ?? {
			sort: defaultSort,
			filters: [],
			search: null
		}
	);
}

/**
 * Set the search query for an infinity list
 */
export function setInfinitySearch(listId: string, search: string | null) {
	infinityListStore.update((store) => {
		const list = store[listId] ?? { sort: null, filters: [], search: null };
		return {
			...store,
			[listId]: {
				...list,
				search
			}
		};
	});
}

/**
 * Set the sort configuration for an infinity list
 */
export function setInfinitySort(listId: string, sort: InfinityListSort | null) {
	infinityListStore.update((store) => {
		const list = store[listId] ?? { sort: null, filters: [], search: null };
		return {
			...store,
			[listId]: {
				...list,
				sort
			}
		};
	});
}

/**
 * Set applied filters array for an infinity list
 */
export function setInfinityFilters(listId: string, filters: AppliedFilter[]) {
	infinityListStore.update((store) => {
		const list = store[listId] ?? { sort: null, filters: [], search: null };
		return {
			...store,
			[listId]: {
				...list,
				filters: [...filters]
			}
		};
	});
}

/**
 * Clear all filters for an infinity list
 */
export function clearInfinityFilters(listId: string) {
	setInfinityFilters(listId, []);
}

/**
 * Reset an infinity list (filters, search and optionally sort)
 */
export function resetInfinityList(listId: string, defaultSort: InfinityListSort | null = null) {
	infinityListStore.update((store) => {
		return {
			...store,
			[listId]: {
				sort: defaultSort,
				filters: [],
				search: null
			}
		};
	});
}

/**
 * Trigger a reload for an infinity list
 */
export function reloadInfinityList(listId: string) {
	infinityListReloadStore.update((store) => {
		store[listId] = true;
		return store;
	});
}

/**
 * Trigger a reload for all active infinity lists
 */
export function reloadAllInfinityLists() {
	infinityListReloadStore.update((store) => {
		for (const key in store) {
			store[key] = true;
		}
		return store;
	});
}

/**
 * Check if a reload has been requested for an infinity list
 */
export function isInfinityReloadRequested(listId: string): boolean;
export function isInfinityReloadRequested(store: InfinityListReloadStore, listId: string): boolean;
export function isInfinityReloadRequested(
	storeOrListId: InfinityListReloadStore | string,
	listId?: string
): boolean {
	if (typeof storeOrListId === 'string') {
		return Boolean(get(infinityListReloadStore)[storeOrListId]);
	}
	return Boolean(storeOrListId[listId ?? '']);
}

/**
 * Clear the reload request flag for an infinity list
 */
export function clearInfinityReload(listId: string) {
	infinityListReloadStore.update((store) => {
		store[listId] = false;
		return store;
	});
}

/**
 * Get items for an infinity list (safe read with fallback to empty array)
 */
export function getInfinityListItems<T = unknown>(listId: string): T[];
export function getInfinityListItems<T = unknown>(
	store: InfinityListItemStore,
	listId: string
): T[];
export function getInfinityListItems<T = unknown>(
	storeOrListId: InfinityListItemStore | string,
	listId?: string
): T[] {
	if (typeof storeOrListId === 'string') {
		const items = get(infinityListItemStore)[storeOrListId];
		return (items as T[] | undefined) ?? [];
	}
	return (storeOrListId[listId ?? ''] as T[] | undefined) ?? [];
}

/**
 * Set cached items for an infinity list
 */
export function setInfinityListItems<T = unknown>(listId: string, items: T[]) {
	infinityListItemStore.update((store) => {
		store[listId] = items;
		return store;
	});
}

const DEFAULT_ITEM_COUNT: InfinityItemCount = {
	count: 0,
	ended: false
};

/**
 * Get count and ended status for an infinity list (safe fallback)
 */
export function getInfinityItemCount(listId: string): InfinityItemCount;
export function getInfinityItemCount(
	store: InfinityListItemCountStore,
	listId: string
): InfinityItemCount;
export function getInfinityItemCount(
	storeOrListId: InfinityListItemCountStore | string,
	listId?: string
): InfinityItemCount {
	if (typeof storeOrListId === 'string') {
		return get(infinityItemListCountStore)[storeOrListId] ?? DEFAULT_ITEM_COUNT;
	}
	return storeOrListId[listId ?? ''] ?? DEFAULT_ITEM_COUNT;
}

/**
 * Set count and ended status for an infinity list
 */
export function setInfinityItemCount(listId: string, count: number, ended: boolean) {
	infinityItemListCountStore.update((store) => {
		store[listId] = { count, ended };
		return store;
	});
}

// region Filter Methods

export function hasNonDefaultFilter(listId: string, filters: InfinityListFilters): boolean;
export function hasNonDefaultFilter(
	store: InfinityListStore,
	listId: string,
	filters: InfinityListFilters
): boolean;
export function hasNonDefaultFilter(
	storeOrListId: InfinityListStore | string,
	listIdOrFilters: string | InfinityListFilters,
	filtersOrUndefined?: InfinityListFilters
): boolean {
	const list =
		typeof storeOrListId === 'string'
			? getInfinityList(storeOrListId)
			: getInfinityList(storeOrListId, listIdOrFilters as string);

	const filters =
		typeof storeOrListId === 'string'
			? (listIdOrFilters as InfinityListFilters)
			: (filtersOrUndefined as InfinityListFilters);

	for (const key in filters) {
		const filter = getAppliedFilter(list, key);

		if (filter && filter.value !== filters[key].default) {
			return true;
		}
	}

	return false;
}

export function restoreDefaultFilters(listId: string, filters: InfinityListFilters) {
	const list = getInfinityList(listId);

	for (const key in filters) {
		const filter = getAppliedFilter(list, key);

		if (filter && filter.value !== filters[key].default) {
			unsetInfinityFilter(listId, key);
		}
	}
}

/**
 * Get the desired applied filter from the list
 * @param list The list to get the filter from
 * @param filterName The name of the filter to get
 */
export function getAppliedFilter(
	list: InfinityList | null | undefined,
	filterName: string
): AppliedFilter | null {
	return list?.filters.find((f) => f.name === filterName) || null;
}

/**
 * Remove a filter from the filter list
 */
export function unsetInfinityFilter(listId: string, key: string) {
	infinityListStore.update((store) => {
		const current = store[listId];
		if (!current) return store;

		return {
			...store,
			[listId]: {
				...current,
				filters: current.filters.filter((f) => f.name !== key)
			}
		};
	});
}

/**
 * Define a filter in the filter list
 * If no value is provided, the filter will be considered as a flag
 */
export function setInfinityFilter(listId: string, key: string, value?: string | string[]) {
	infinityListStore.update((store) => {
		const current = store[listId] ?? { sort: null, filters: [], search: null };
		const existingIndex = current.filters.findIndex((f) => f.name === key);
		const newFilters = [...current.filters];

		if (existingIndex >= 0) {
			newFilters[existingIndex] = {
				name: key,
				value: value !== undefined ? value : null
			};
		} else {
			newFilters.push({
				name: key,
				value: value !== undefined ? value : null
			});
		}

		return {
			...store,
			[listId]: {
				...current,
				filters: newFilters
			}
		};
	});
}

/**
 * Add a value to a value filter
 */
export function addInfinityFilterValue(listId: string, key: string, value: string) {
	const list = getInfinityList(listId);
	const filter = getAppliedFilter(list, key);

	if (!filter) {
		setInfinityFilter(listId, key, [value]);
		return;
	}

	if (filter.value) {
		if (!Array.isArray(filter.value)) {
			console.error('Cannot add value to a flag filter');
			return;
		}

		if (!filter.value.includes(value)) {
			infinityListStore.update((store) => {
				const current = store[listId];
				if (current) {
					current.filters = current.filters.map((f) => {
						if (f.name === key) {
							f.value = [...(f.value as string[]), value];
						}
						return f;
					});
					store[listId] = current;
				}
				return store;
			});
		}
	}
}

/**
 * Remove a value from a value filter
 */
export function removeInfinityFilterValue(listId: string, key: string, value: string) {
	const list = getInfinityList(listId);
	const filter = getAppliedFilter(list, key);

	if (!filter) {
		setInfinityFilter(listId, key, [value]);
		return;
	}

	if (filter.value) {
		if (!Array.isArray(filter.value)) {
			console.error('Cannot remove value to a flag filter');
			return;
		}

		if (filter.value.includes(value)) {
			infinityListStore.update((store) => {
				const current = store[listId];
				if (current) {
					current.filters = current.filters.map((f) => {
						if (f.name === key) {
							f.value = (f.value as string[]).filter((v) => v !== value);
						}
						return f;
					});
					store[listId] = current;
				}
				return store;
			});
		}
	}
}

// endregion

// region List Freshness Methods
/**
 * Mark a list as not fresh unless the item exists in the list.
 * @param itemId The id of the item to check
 * @param itemIdKey The key of the item id in the item object
 * @param store The store to mark as not fresh
 */
export function markListAsNotFreshUnlessExists<T>(
	itemId: string | number,
	itemIdKey: keyof T,
	store: Writable<ListStore<T>>
) {
	const storeData = get(store);

	// Only mark as not fresh if the item is NOT currently in the store data
	if (storeData.data.find((item) => item[itemIdKey] === itemId)) {
		return;
	}

	store.update((store) => {
		store.isFresh = false;
		return store;
	});
}

// endregion
